import { LogOut } from "lucide-react";
import { useState } from "react";
import { useApiClient } from "../api/ApiClientContext.js";

export interface LogoutButtonProps {
  /** `menu` — пункт в выпадающем меню, `icon` — кнопка в панели, `text` — обычная кнопка. */
  readonly variant?: "menu" | "icon" | "text";
  readonly className?: string;
  /** Выйти на всех устройствах: месячная кука на потерянном телефоне гасится только так. */
  readonly allDevices?: boolean;
  readonly label?: string;
}

/**
 * Выход из сессии.
 *
 * Сессия живёт месяц (`SESSION_TTL_SECONDS`), поэтому кнопка обязательна: без неё нельзя ни
 * сменить роль, ни выйти на чужом устройстве, ни выбраться из зависшей сессии. После ответа
 * страница перезагружается целиком — так гарантированно уходит и кэш запросов, и состояние
 * поверхности, а человек попадает на экран входа.
 */
export function LogoutButton(props: LogoutButtonProps) {
  const api = useApiClient();
  const [busy, setBusy] = useState(false);
  const label = props.label ?? (props.allDevices ? "Выйти на всех устройствах" : "Выйти");

  const logout = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.logout(props.allDevices ? { allDevices: true } : {});
    } catch {
      // Даже если сервер не ответил, перезагрузка вернёт на экран входа: куки уже могли уйти.
    } finally {
      if (typeof window !== "undefined") window.location.assign("/");
      else setBusy(false);
    }
  };

  if (props.variant === "icon") {
    return (
      <button
        type="button"
        className={`inline-flex size-11 items-center justify-center rounded-xl text-ink-700 transition-colors hover:bg-pad-200 hover:text-ink-900 ${props.className ?? ""}`}
        aria-label={label}
        title={label}
        disabled={busy}
        data-testid="logout"
        onClick={() => void logout()}
      >
        <LogOut size={18} aria-hidden="true" />
      </button>
    );
  }

  return (
    <button
      type="button"
      className={
        props.variant === "menu"
          ? `flex min-h-11 w-full items-center gap-2 px-2 text-left text-sm ${props.className ?? ""}`
          : `pwz-btn pwz-btn-ghost ${props.className ?? ""}`
      }
      disabled={busy}
      data-testid="logout"
      onClick={() => void logout()}
    >
      <LogOut size={16} aria-hidden="true" />
      {busy ? "Выходим…" : label}
    </button>
  );
}
